<template>
  <div class="component-wrapper" id="application-detail" :class="[showStepper]">
    <div class="xpage-top d-flex">
      <div class="xpage-top-left" :class="[{ hasStepper: showStepper }]">
        <div class="xpage-top-row" id="name">
          <div class="xpage-top-profile">
            <div class="xpage-top-profile-icon">
              {{ summary.primaryApplicantInitials }}
            </div>
          </div>
          <div class="xpage-top-col">
            <div class="xpage-top-fullName">
              <h2
                :title="summary.primaryApplicantFullName"
                class="applicant-name fullname-wrap text-overflow-ellipsis"
								v-title-overflow
              >
                {{ summary.primaryApplicantFullName || "No Name" }}
              </h2>
            </div>
            <div class="xpage-top-col-section">
              <div class="xpage-top-lenderName">
                <h3 :title="summary.lenderName" v-title-overflow>
                  {{ summary.lenderName || "No Lender Provided" }}
                </h3>
              </div>
              <div
                v-if="applicationStore.isParentLOC"
                class="icon-student student-info xpage-top-studentName"
                id="student"
              >
                <span>&nbsp;Student:</span
                ><span
                  class="text-wrap-auto"
                  :title="
                    summary.studentFullName
                      ? summary.studentFullName
                      : undefined
                  "
                >
                  &nbsp;{{ summary.studentFullName }}</span
                >
              </div>
              <div class="xpage-top-status-dropdown">
                <PermissionsWrapper
                  :showDisabledIfNotVisible="true"
                  :permissionIds="[Permissions.ProgramConfiguration]"
                  :permissionLevel="PermissionLevels.Update"
                >
                  <application-status />
                </PermissionsWrapper>
              </div>
              <div
                v-if="showMoveToLoanService"
                class="xpage-top-details-status"
                v-auto-animate="{ ...$animate, duration: 100 }"
              >
                <button
                  type="submit"
                  class="moveToLoanService-btn"
                  @click="moveToLoanService(true)"
                >
                  Move To Loan Service »
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="xpage-top-right application-detail-details-stepper"
        v-if="showStepper"
      >
        <details-stepper />
      </div>
    </div>
    <div class="xcollapse-mobile">
      <a
        href="#"
        :class="toggleIconClass"
        @click.prevent="toggleApplicationDetailsMobile"
      ></a>
    </div>
    <div
      class="application-summary-wrapper"
      :class="{ xhideformobile: mobileHidden }"
    >
      <div class="application-summary section-1">
        <div class="summary-items">
          <div class="summary-item">
            <div class="label">Line of Credit</div>
            <div class="content">{{ LOCLimit }}</div>
            <div class="content">Requested {{ summary.initialDrawAmount }}</div>
          </div>
          <div class="summary-item">
            <div class="label">Anticipated Graduation</div>
            <div class="content">{{ summary.graduationDate }}</div>
            <div class="content">
              {{ applicationTab.school?.schoolName || "No School Provided" }}
            </div>
          </div>
          <div class="summary-item">
            <div class="label">Interest Rate</div>
            <div class="content">{{ summary.rate }}</div>
            <div class="content">{{ summary.rateTypeName }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, ref } from "vue";
import { storeToRefs } from "pinia";
import ApplicationStatus from "@/components/v2/application/ApplicationStatus.vue";
import { Permissions, PermissionLevels } from "@/models/permissions";
import PermissionsWrapper from "../../../wrappers/PermissionsWrapper.vue";
import { useApplicationStore } from "@/store/store/applicationVMStore";
import { useApplicationDetails } from "@/composables/application/useApplicationDetails";
import DetailsStepper from "@/components/v2/application/DetailsStepper.vue";
import functions from "@/use/functions";
import { useFeatures } from "@/use/useFeatures";

const applicationStore = useApplicationStore();
const { summary, applicationTab } = storeToRefs(applicationStore);
const { moveToLoanService } = useApplicationDetails();

const { features, loadFeatures } = useFeatures();

const mobileHidden = ref<boolean>(true);

const showMoveToLoanService = computed(() => {
  return (
    applicationStore.viewModel.statusName === "Complete" &&
    !applicationStore.viewModel.loanId
  );
});

const toggleIconClass = computed(() =>
  mobileHidden.value ? "icon-down-open-big" : "icon-up-open-big"
);

const LOCLimit = computed<string>(() => {
  return summary.value.loanAmount > 0 ? summary.value.loanAmountDisplay : "—";
});

const toggleApplicationDetailsMobile = () => {
  mobileHidden.value = !mobileHidden.value;
};

const showStepper = computed<boolean>(() => {
  return features.value?.locTimeline === true;
});

onMounted(() => {
  loadFeatures();
  functions.getType("ApplicationStatus");
});
</script>

<style scoped>
* {
  --stepperContainerWidth: 500px;
}
.moveToLoanService-btn {
  background: #48a842;
  color: white;
  line-height: 26px;
  font-weight: 600;
  border: 0;
  padding: 0 12px;
  border-radius: 6px;
}

.moveToLoanService-btn:hover {
  background: #0ecb8e;
}

.component-wrapper#application-detail .xpage-top {
  border: none !important;
  border-bottom: none !important;
  box-shadow: none !important;
}

.component-wrapper#application-detail {
  width: 100%;
  background-color: #e2e4e9;
  padding-left: 22px;
  padding-right: 22px;
  padding-top: 0;
  padding-bottom: 0;
}

.application-detail-details-stepper {
  width: 500px;
  height: 108px;
  position: absolute;
  top: 22px;
  right: 0;
}

.application-summary-wrapper {
  border: none;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0px 0px 20px 22px;
  row-gap: 22px;
  position: relative;
}

.application-summary {
  border: none;
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: 0;
  flex-wrap: wrap;
}

.application-summary.section-1 {
  width: calc(100% - 500px);
  display: flex;
  flex-direction: row;
}

.application-summary.section-1 .summary-items {
  display: flex;
  width: 100%;
  column-gap: 50px;
  row-gap: 22px;
  flex-wrap: wrap;
}

.application-summary.section-1 .summary-item {
  max-width: calc(100% / 3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

div.label {
  color: #000;
  font-family: var(--sc-montserrat-font-family);
  font-size: 14px;
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: normal;
  text-transform: uppercase;
  margin-bottom: 5px;
}

.application-summary .summary-item .content {
  color: #000;
  margin-bottom: 5px;
  font-size: 14px;
  font-weight: var(--font-weight-medium);
  line-height: normal;
}

.xpage-top {
  &.d-flex {
    width: 100%;
    display: flex;
    padding-right: 0;
  }
}
.xpage-top-left {
  width: 100%;

  &.hasStepper {
    width: calc(100% - var(--stepperContainerWidth));
  }
}

.xpage-top-right {
  width: 500px;
  padding-top: 0;
}

.xpage-top-fullName {
  max-width: calc(100%);
}

.xpage-top-col {
  width: calc(100% - 112px - 20px);
}

@media screen and (max-width: 1270px) {
  .application-detail-details-stepper {
    position: relative;
    width: 100%;
    background: transparent;
    border: none;
  }

  .xpage-top-row {
    display: block !important;
  }
  .xpage-top-status-dropdown,
  .xpage-top-studentName,
  .xpage-top-lenderName {
    margin-left: 0;
  }
  .xpage-top-left {
    &.hasStepper {
      width: 100%;
      .xpage-top-row {
        display: block;
      }

      .xpage-top-col {
        width: 100%;
      }
    }
  }

  .xpage-top-col-section {
    margin-left: 112px;
  }
}

@media screen and (max-width: 1280px) {
  .xpage-top.d-flex {
    flex-direction: column;
  }
}
@media screen and (max-width: 1150px) {
  .application-summary.section-1 {
    width: 100%;
  }
}

@media screen and (max-width: 900px) {
  .application-detail-details-stepper {
    position: relative;
    width: 100%;
    background: transparent;
    border: none;
  }

  .application-summary.section-1 .summary-item {
    max-width: 100%;
  }

  .application-summary-wrapper {
    padding-left: 0;
    margin-top: 20px;
  }

  .xpage-top.d-flex {
    flex-direction: column;
  }

  .xpage-top-row {
    display: block !important;
  }

  .xpage-top-col {
    width: 100%;
  }
  .xpage-top-row:not(#name) {
    margin-left: 0px;
  }

  .xpage-top-status-dropdown,
  .xpage-top-studentName,
  .xpage-top-lenderName {
    margin-left: 0px;
  }

  .xpage-top-col-section {
    margin-left: 0px;
  }
}
</style>

<style scoped src="@/assets/css/application-details/student.css"></style>
